import {
  Badge,
  Button,
  Checkbox,
  Flex,
  FormatDate,
  Icon,
  Modal,
  Text,
  Tooltip,
} from '@wyz/ui';
import { DateUtils, OFFER_TYPE } from '@wyz/utils';
import * as React from 'react';
import { TextField } from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { IAcceptOrderItemPayload, ISuggestedOffer } from '~/purchase/types';

type Props = {
  offers: ISuggestedOffer[];
  watchedItems: IAcceptOrderItemPayload[];
  variant: 'ORDERABLE' | 'DESTOCKING';
  onConfirm: () => void;
  onClose: () => void;
};

type VariantProps = Omit<Props, 'variant'>;

const getQuantity = (
  watchedItems: IAcceptOrderItemPayload[],
  offerId: string,
): number =>
  watchedItems.find((item) => item.lineId === offerId)?.quantity ?? 0;

const DestockingConfirmOrderModal = ({
  offers,
  watchedItems,
  onConfirm,
  onClose,
}: VariantProps) => {
  const { t } = useI18n();
  const locale = useLocale();
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [showAcknowledgeError, setShowAcknowledgeError] = React.useState(false);

  const handleConfirm = () => {
    if (!acknowledged) {
      setShowAcknowledgeError(true);

      return;
    }
    onConfirm();
  };

  return (
    <Modal.Container
      open
      size='lg'
      backdrop='static'
      centered
      onClose={onClose}
    >
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.redistribution.order_accept.destocking_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='bg-warning-subtle border border-warning rounded p-3 mb-4'>
          <Flex className='space-x-2'>
            <Text component='span' color='warning'>
              <Icon icon='info-circle' />
            </Text>
            <Text heading='sm' weight='bold' color='warning'>
              <Trans i18nKey='app.purchase.redistribution.order_accept.destocking_modal.dms_warning' />
            </Text>
          </Flex>
        </div>
        <div className='bg-neutral-grey-2 p-1 mb-2'>
          <table>
            <thead>
              <th className='text-center p-2 col-3'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.supplier' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.orders.delivery_date' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.buying_pricing_unit' />
                </Text>
              </th>
            </thead>
            <tbody>
              {/* a destocking order is entirely local stock, so every offer is
                  DISTRIBUTOR_PLATFORM: filtering those out would empty the modal */}
              {offers.map((suggestedOffer, offerIndex) => {
                const quantity = getQuantity(
                  watchedItems,
                  suggestedOffer.offerId,
                );
                if (!quantity) {
                  return <></>;
                }
                return (
                  <tr key={offerIndex}>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`supplier-${suggestedOffer.offer.supplier.name}`}
                      >
                        {suggestedOffer.offer.supplier.name}
                        <Badge bg='marigold' className='ms-1'>
                          <Icon icon='tag' />
                        </Badge>
                      </Text>
                    </td>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`supplier-${suggestedOffer.offer.supplier.name}-quantity`}
                      >
                        {quantity}
                      </Text>
                    </td>
                    <td className='text-center p-2 no-border'>
                      <Text
                        id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-delivery`}
                        heading='xs'
                        className=' shadow-none border-0 '
                        weight='semi-bold'
                      >
                        <FormatDate
                          date={suggestedOffer.offer.deliveryDate}
                          format={DateUtils.DATE_FORMAT.DMY}
                          locale={locale.locale}
                        />
                      </Text>
                    </td>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-priceC`}
                      >
                        {locale.formatters.money(
                          suggestedOffer.offer.redistributionBuyingPrice,
                          {
                            fallback: '-',
                            currency: suggestedOffer.offer.currencyIsoCode,
                          },
                        )}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className='bg-neutral-grey-2 p-1'>
          <div className='p-2'>
            <Flex className='space-x-2'>
              <Text component='span' color='primary'>
                <Icon icon='chat-line' size='xs' />
              </Text>
              <Text heading='sm' weight='bold'>
                <Trans i18nKey='app.common.comment' />
              </Text>
            </Flex>
            <TextField
              name='comment'
              type='text'
              as='textarea'
              placeholder={t(
                'app.purchase.orders.pending.confirm_modal.comment.placeholder',
              )}
              rows={4}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className='w-100'>
          <div className='mb-3'>
            <Checkbox
              id='destocking-dms-acknowledgement'
              name='destockingDmsAcknowledgement'
              checked={acknowledged}
              isInvalid={showAcknowledgeError && !acknowledged}
              onChange={(event) => {
                setAcknowledged(event.currentTarget.checked);
                setShowAcknowledgeError(false);
              }}
              label={
                <Text
                  component='span'
                  heading='sm'
                  color={
                    showAcknowledgeError && !acknowledged ? 'danger' : undefined
                  }
                >
                  <Text component='span' color='danger' translate='no'>
                    {'* '}
                  </Text>
                  <Trans i18nKey='app.purchase.redistribution.order_accept.destocking_modal.acknowledgement' />
                </Text>
              }
            />
          </div>
          <Button
            id='destocking-accept-order-modal-btn'
            variant='primary'
            onClick={handleConfirm}
            fullWidth
          >
            <Trans i18nKey='app.common.button.accept' />
          </Button>
        </div>
      </Modal.Footer>
    </Modal.Container>
  );
};

const OrderableConfirmOrderModal = ({
  offers,
  watchedItems,
  onConfirm,
  onClose,
}: VariantProps) => {
  const { t } = useI18n();
  const locale = useLocale();

  const visibleOffers = offers.filter(
    (suggestedOffer) =>
      suggestedOffer.offer.offerType !== OFFER_TYPE.DISTRIBUTOR_PLATFORM,
  );

  const hasOrderableSuppliers = offers.some(
    (suggestedOffer) =>
      suggestedOffer.offer.supplier.orderable === true &&
      suggestedOffer.offer.offerType !== OFFER_TYPE.DISTRIBUTOR_PLATFORM,
  );

  return (
    <Modal.Container open backdrop='static' centered onClose={onClose}>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.redistribution.not_orderable_orders.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='w-100 bg-white'>
          <Text heading='xs' weight='normal' className='mb-4'>
            {!hasOrderableSuppliers
              ? t('app.purchase.redistribution.not_orderable_orders.text')
              : t('app.purchase.redistribution.orderable_orders.text')}
          </Text>
        </div>
        <div className='bg-neutral-grey-2 p-1 mb-2'>
          <table>
            <thead>
              <th className='text-center p-2 col-3'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.supplier' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.orders.delivery_date' />
                </Text>
              </th>
              <th className='p-2 text-center col-1'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.buying_pricing_unit' />
                </Text>
              </th>
              <th className='p-2 text-center col-4'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.status' />
                </Text>
              </th>
            </thead>
            <tbody>
              {visibleOffers.map((suggestedOffer, offerIndex) => {
                const quantity = getQuantity(
                  watchedItems,
                  suggestedOffer.offerId,
                );
                if (!quantity) {
                  return <></>;
                }
                return (
                  <tr key={offerIndex}>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`supplier-${suggestedOffer.offer.supplier.name}`}
                      >
                        {suggestedOffer.offer.supplier.name}
                      </Text>
                    </td>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`supplier-${suggestedOffer.offer.supplier.name}-quantity`}
                      >
                        {quantity}
                      </Text>
                    </td>
                    <td className='text-center p-2 no-border'>
                      <Text
                        id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-delivery`}
                        heading='xs'
                        className=' shadow-none border-0 '
                        weight='semi-bold'
                      >
                        {suggestedOffer.offer.isBackOrder ? (
                          t('app.common.offer_type.backorder')
                        ) : (
                          <FormatDate
                            date={suggestedOffer.offer.deliveryDate}
                            format={DateUtils.DATE_FORMAT.DM}
                            locale={locale.locale}
                          />
                        )}
                      </Text>
                    </td>
                    <td className='text-center p-1 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`${offerIndex}-${suggestedOffer.offer.supplier.name}-priceC`}
                      >
                        {locale.formatters.money(
                          suggestedOffer.offer.redistributionBuyingPrice,
                          {
                            fallback: '-',
                            currency: suggestedOffer.offer.currencyIsoCode,
                          },
                        )}
                      </Text>
                    </td>
                    <td className='text-center p-2 no-border'>
                      <Text
                        heading='xs'
                        weight='semi-bold'
                        className=' shadow-none border-0 '
                        id={`supplier-${suggestedOffer.offer.supplier.name}-status`}
                      >
                        <Badge
                          bg={
                            suggestedOffer.offer.supplier.orderable === false
                              ? 'warning'
                              : 'success'
                          }
                          pill
                          className='text-xs'
                        >
                          <Trans
                            i18nKey={
                              suggestedOffer.offer.supplier.orderable === false
                                ? 'app.common.status.not_orderable'
                                : 'app.common.status.orderable'
                            }
                          />
                        </Badge>
                        {suggestedOffer.offer.supplier.orderable === false && (
                          <Tooltip
                            label={
                              <Trans i18nKey='app.purchase.redistribution.pendin_order.accept.not_orderable_tooltip' />
                            }
                            style={{ maxWidth: '300px' }}
                          >
                            <Text component='span' color='danger'>
                              <Icon icon='error-warning-line' />
                            </Text>
                          </Tooltip>
                        )}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasOrderableSuppliers && (
          <div className='bg-neutral-grey-2 p-1'>
            <div className='p-2'>
              <TextField
                name='comment'
                type='text'
                as='textarea'
                label={<Trans i18nKey='app.common.comment' />}
                placeholder={t(
                  'app.purchase.orders.pending.confirm_modal.comment.placeholder',
                )}
                rows={3}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!hasOrderableSuppliers ? (
          <Button
            id='i-understand-modal-btn'
            variant='primary'
            onClick={onConfirm}
            fullWidth
          >
            <Trans i18nKey='app.common.button.i_understand' />
          </Button>
        ) : (
          <Button
            id='i-accept-and-order-modal-btn'
            variant='primary'
            onClick={onConfirm}
            fullWidth
          >
            <Trans i18nKey='app.purchase.redistribution.pending_order.accept_with_order_modal_btn' />
          </Button>
        )}
      </Modal.Footer>
    </Modal.Container>
  );
};

const ConfirmOrderModal = ({ variant, ...props }: Props) =>
  variant === 'DESTOCKING' ? (
    <DestockingConfirmOrderModal {...props} />
  ) : (
    <OrderableConfirmOrderModal {...props} />
  );

export default ConfirmOrderModal;
